import { Form, message } from "antd";
import { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import type {
  KycQuestionType,
  KycQuestionnaireQuestionInput,
} from "../../graphql";
import sdk from "../../sdk";

export type SubQuestionFormItem = {
  type: KycQuestionType;
  question_mn: string;
  question_en: string;
  required: boolean;
  options: OptionFormItem[];
  order: number;
};

export type OptionFormItem = {
  value_mn: string;
  value_en: string;
  requiresInput: boolean;
  triggersSubQuestions: boolean;
};

export type QuestionFormItem = {
  type: KycQuestionType;
  question_mn: string;
  question_en: string;
  required: boolean;
  options: OptionFormItem[];
  subQuestions: SubQuestionFormItem[];
  order: number;
};

function mapOption(o: OptionFormItem) {
  return {
    value: o.value_mn,
    localizedValue: { en: o.value_en, mn: o.value_mn },
    requiresInput: o.requiresInput,
    triggersSubQuestions: o.triggersSubQuestions,
  };
}

function mapSubQuestion(
  sq: SubQuestionFormItem,
  index: number,
): KycQuestionnaireQuestionInput {
  return {
    type: sq.type,
    question: sq.question_mn,
    localizedQuestion: { en: sq.question_en, mn: sq.question_mn },
    required: sq.required,
    options:
      sq.type === "SINGLE_CHOICE" || sq.type === "MULTIPLE_CHOICE"
        ? sq.options.map(mapOption)
        : undefined,
    order: index,
  };
}

function mapQuestion(
  q: QuestionFormItem,
  index: number,
): KycQuestionnaireQuestionInput {
  return {
    type: q.type,
    question: q.question_mn,
    localizedQuestion: { en: q.question_en, mn: q.question_mn },
    required: q.required,
    options:
      q.type === "SINGLE_CHOICE" || q.type === "MULTIPLE_CHOICE"
        ? q.options.map(mapOption)
        : undefined,
    subQuestions:
      q.subQuestions.length > 0 ? q.subQuestions.map(mapSubQuestion) : undefined,
    order: index,
  };
}

export const useKycQuestionnaireForm = ({
  selectedId,
}: {
  selectedId?: string;
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState<QuestionFormItem[]>([]);
  const history = useHistory();

  const validateQuestions = useCallback((): string | null => {
    if (questions.length === 0) {
      return "Дор хаяж нэг асуулт нэмнэ үү";
    }
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.question_mn.trim()) {
        return `Асуулт ${i + 1}-ийн монгол текстийг бөглөнө үү`;
      }
      if (!q.question_en.trim()) {
        return `Асуулт ${i + 1}-ийн англи текстийг бөглөнө үү`;
      }
      if (q.type === "SINGLE_CHOICE" || q.type === "MULTIPLE_CHOICE") {
        if (q.options.length === 0) {
          return `Асуулт ${i + 1}-д дор хаяж нэг сонголт нэмнэ үү`;
        }
        for (let j = 0; j < q.options.length; j++) {
          const o = q.options[j];
          if (!o.value_mn.trim()) {
            return `Асуулт ${i + 1}, сонголт ${j + 1}-ийн МН-г бөглөнө үү`;
          }
          if (!o.value_en.trim()) {
            return `Асуулт ${i + 1}, сонголт ${j + 1}-ийн EN-г бөглөнө үү`;
          }
        }
      }
    }
    return null;
  }, [questions]);

  const addKycQuestionnaire = useCallback(
    (values: Record<string, string | boolean>) => {
      const err = validateQuestions();
      if (err) {
        message.warn(err, 2);
        return;
      }
      setLoading(true);
      sdk
        .addKycQuestionnaire({
          input: {
            name: values.name_mn as string,
            localizedName: {
              en: values.name_en as string,
              mn: values.name_mn as string,
            },
            description: values.description_mn as string,
            localizedDescription: {
              en: values.description_en as string,
              mn: values.description_mn as string,
            },
            active: (values.active as boolean) ?? false,
            questions: questions.map(mapQuestion),
          },
        })
        .then(() => {
          message.success("Амжилттай нэмэгдлээ", 2);
          history.push("/kyc-questionnaires");
        })
        .catch((err) => {
          message.warn(err.response?.errors[0]?.message, 2);
        })
        .finally(() => setLoading(false));
    },
    [questions, history, validateQuestions],
  );

  const editKycQuestionnaire = useCallback(
    (values: Record<string, string | boolean>) => {
      if (!selectedId) return;
      const err = validateQuestions();
      if (err) {
        message.warn(err, 2);
        return;
      }
      setLoading(true);
      sdk
        .editKycQuestionnaire({
          _id: selectedId,
          input: {
            name: values.name_mn as string,
            localizedName: {
              en: values.name_en as string,
              mn: values.name_mn as string,
            },
            description: values.description_mn as string,
            localizedDescription: {
              en: values.description_en as string,
              mn: values.description_mn as string,
            },
            active: (values.active as boolean) ?? false,
            questions: questions.map(mapQuestion),
          },
        })
        .then(() => {
          message.success("Амжилттай засагдлаа", 2);
        })
        .catch((err) => {
          message.warn(err.response?.errors[0]?.message, 2);
        })
        .finally(() => setLoading(false));
    },
    [questions, selectedId, validateQuestions],
  );

  return {
    form,
    loading,
    questions,
    setQuestions,
    addKycQuestionnaire,
    editKycQuestionnaire,
  };
};
